import { Keys } from "@/constants/key";
import { Tr } from "@/i18n/i18n";
import { SceneName } from "@/model/scene-name";
import { PagingPanel } from "@/scenes.ui.forum/paging-panel";
import { ConfirmationPanel } from "@/scenes.ui/confirmation-panel";
import { FeedbackPanel } from "@/scenes.ui/feedback-panel";
import { LogPanel } from "@/scenes.ui/log-panel";
import { PaymentAdminPanel } from "@/scenes.ui/payment-admin-panel";
import { PlayerActionButton } from "@/scenes.ui/player-action-button";
import { PlayerHistorySettings } from "@/scenes.ui/player-history-settings";
import ProfilePanel from "@/scenes.ui/profile-panel";
import { ListService } from "@/services/list-service";
import { MenuService } from "@/services/menu-service";
import ModerStore from "@/store/moderator";
import { BanReason } from "@/store/moderator/types";
import PlayerStore from "@/store/player";
import { HistoryDto, InterfaceMode, LogType, PaymentsDto, Player, PurchaseType } from "@/store/player/types";
import ProfileStore from "@/store/profile";
import { Profile } from "@/store/profile/types";
import CU from "@/utils/c-u";
import { ListBlackouter } from "@/utils/list-blackouter";
import PlayerUtils from "@/utils/player-utils";
import SpriteUtils from "@/utils/sprite-utils";
import { injectable } from "inversify";
import Scene = Phaser.Scene;
import Container = Phaser.GameObjects.Container;

@injectable()
export class ProfileService {

    public openProfile(scene: Scene, canvas: HTMLCanvasElement, closeCallback: () => void,
                       onSpeedButtonPressCallback: (moveSpeedButton: PlayerActionButton) => unknown,
                       onSpeedButtonInit: (moveSpeedButton: PlayerActionButton) => unknown,
                       onCreaturePanelPressCallback: (sceneName: SceneName, params: any) => void,
                       onMailButtonPressCallback: (id: number, support: boolean) => void,
                       playerId: number,
                       onSoundButtonPressCallback: (soundButton: PlayerActionButton) => void,
                       onSoundButtonInit: (soundButton: PlayerActionButton) => unknown,
                       onGenderButtonPressCallback: () => unknown,
                       onPaymentButtonPressCallback: () => unknown,
                       onRenameButtonPressCallback: () => unknown,
                       onEmailOrPhoneButtonPressCallback: () => unknown,
                       onPasswordButtonPressCallback: () => unknown,
                       onManualButtonPressCallback: () => unknown,
                       reloadSceneCallback: () => unknown,
                       giftGemsCallback: (receiver: Profile) => unknown,
                       listService: ListService,
                       menuService: MenuService) {

        const blackouter: ListBlackouter = listService.showSmart(scene, canvas, closeCallback);
        const self = this;

        this.getProfile(playerId, (profile: Profile) => {

            blackouter.notifyLoaded(() => {
                const profilePanel: ProfilePanel = new ProfilePanel(scene!, canvas!, profile,
                    onSpeedButtonPressCallback, onSpeedButtonInit, onCreaturePanelPressCallback,
                    onMailButtonPressCallback, (timeInSeconds: number, banReason: BanReason, playerId: number) => this.mutePlayer(scene, banReason, timeInSeconds, playerId),
                    (playerId: number) => this.unmutePlayer(scene, playerId),
                    onSoundButtonPressCallback, onSoundButtonInit, () => {},
                    onGenderButtonPressCallback, onPaymentButtonPressCallback, () => {
                        const player: Player = PlayerStore.getPlayer();
                        const cp = new ConfirmationPanel(scene, canvas, () => {
                            PlayerStore.switchUi((mode: InterfaceMode) => {
                                window.location.reload();
                            });
                        }, Tr('ui.ch.ttl'),
                            player.interfaceMode === InterfaceMode.ORIGINAL ? Tr('ui.nw') : Tr('ui.old'),
                            Keys.UI_GOLDEN_CHECK, Tr('da'), Tr('net'), Keys.ATLAS_PROFILE);
                        cp.openAnimated();
                    }, 
                    () => {
                        const markerName = 'push_weak';
                        let pushWeak = PlayerUtils.hasMarker(markerName);
                        const cp = new ConfirmationPanel(scene, canvas, () => {
                                PlayerStore.switchPushMode(() => {
                                    new FeedbackPanel(scene, pushWeak ? Tr('lvlg.weak.st') : Tr('lvlg.comb.st'), undefined, true).show();
                                    pushWeak = !pushWeak;
                                });
                            }, Tr('ui.ch.ttl'),
                            pushWeak ? Tr('lvlg.weak') : Tr('lvlg.comb'),
                            Keys.UI_GOLDEN_CHECK, Tr('da'), Tr('net'), Keys.ATLAS_PROFILE);
                        cp.openAnimated();
                    },
                    onRenameButtonPressCallback, onEmailOrPhoneButtonPressCallback, onPasswordButtonPressCallback, onManualButtonPressCallback, reloadSceneCallback, () => {
                        self.showHistory(scene, canvas, playerId, listService, () => {
                            self.openProfile(scene, canvas, closeCallback, onSpeedButtonPressCallback, onSpeedButtonInit,
                                onCreaturePanelPressCallback, onMailButtonPressCallback, playerId, onSoundButtonPressCallback, onSoundButtonInit, onGenderButtonPressCallback, onPaymentButtonPressCallback,
                                onRenameButtonPressCallback, onEmailOrPhoneButtonPressCallback, onPasswordButtonPressCallback, onManualButtonPressCallback, reloadSceneCallback, giftGemsCallback, listService, menuService);
                        });
                    },
                    () => {
                        self.showPayments(scene, canvas, playerId, listService, () => {
                            self.openProfile(scene, canvas, closeCallback, onSpeedButtonPressCallback, onSpeedButtonInit,
                                onCreaturePanelPressCallback, onMailButtonPressCallback, playerId, onSoundButtonPressCallback, onSoundButtonInit, onGenderButtonPressCallback, onPaymentButtonPressCallback,
                                onRenameButtonPressCallback, onEmailOrPhoneButtonPressCallback, onPasswordButtonPressCallback, onManualButtonPressCallback, reloadSceneCallback, giftGemsCallback, listService, menuService);
                        });
                    }, 
                    (playerId: number) => {
                        self.openProfile(scene, canvas, closeCallback, onSpeedButtonPressCallback, onSpeedButtonInit,
                            onCreaturePanelPressCallback, onMailButtonPressCallback, playerId,
                            onSoundButtonPressCallback, onSoundButtonInit, onGenderButtonPressCallback, onPaymentButtonPressCallback,
                            onRenameButtonPressCallback, onEmailOrPhoneButtonPressCallback, onPasswordButtonPressCallback, onManualButtonPressCallback,
                            reloadSceneCallback, giftGemsCallback, listService, menuService);
                    },
                    (receiver: Profile) => giftGemsCallback(receiver));

                const conts: Container[] = [];
                conts.push(profilePanel);

                blackouter.notifyInitialized(conts, profile.displayingName);

                ProfileStore.setProfileSeen();
            });
        });
    }

    private showHistory(scene: Scene, canvas: HTMLCanvasElement, playerId: number, listService: ListService, openProfile: () => void) {
        const blackouter: ListBlackouter = listService.showSmart(scene, canvas, () => {});
        const self = this;

        PlayerStore.setHistoryCategory(undefined);
        PlayerStore.getHistory(playerId, 1).then((h: HistoryDto) => {
            blackouter.notifyLoaded(() => {
                self.initHistory(scene, canvas, playerId, h, blackouter, false, listService, openProfile);
            });
        });
    }

    private showPayments(scene: Scene, canvas: HTMLCanvasElement, playerId: number, listService: ListService, openProfile: () => void){
        const blackouter: ListBlackouter = listService.showSmart(scene, canvas, () => {});
        const self = this;

        PlayerStore.getPayments(playerId, 1).then((h: PaymentsDto) => {
            blackouter.notifyLoaded(() => {
                self.initPayments(scene, playerId, h, blackouter, false, listService, openProfile);
            });
        });
    }

    private initHistory(scene: Scene, canvas:HTMLCanvasElement, playerId: number, h: HistoryDto, blackouter: ListBlackouter, re: boolean, listService:ListService, openProfile: () => void){
        const self = this;
        const conts: Container[] = [];


        for (const l of h.logs) {
            const lp: LogPanel = new LogPanel(scene, l);
            conts.push(lp);
        }

        const pagingPanel = new PagingPanel(scene, h.pageCurr, h.pageMax, (page: number) => {
            PlayerStore.getHistory(playerId, page, PlayerStore.getHistoryCategory()).then((h: HistoryDto) => {
                self.initHistory(scene, canvas, playerId, h, blackouter, true, listService, openProfile);
            });
        }, true);
        conts.push(pagingPanel);

        const buttonCont = new Container(scene, 0, 0);
        const filterButton = SpriteUtils.addSprite(scene, -CU.CANVAS_WIDTH/2 / CU.S + 25, -50,  Keys.ATLAS_FORCES_LARGE, 'ic_rang', 0.8, -0.8);
        filterButton.setInteractive().on('pointerup', function() {
            const infoPanel = new PlayerHistorySettings(scene, canvas, (logType:LogType, purchaseType:PurchaseType) => {
                PlayerStore.setHistoryCategory(logType);
                PlayerStore.getHistory(playerId, 1, logType? logType.toString() : undefined, purchaseType? purchaseType.toString() : undefined).then((h: HistoryDto) => {
                    self.initHistory(scene, canvas, playerId, h, blackouter, true, listService, openProfile);
                });
            });
            infoPanel.openAnimated();
        });
        filterButton.alpha = PlayerStore.getHistoryCategory()? 1 : 0.5;
        buttonCont.add(filterButton);
        conts.push(buttonCont);

        if (re) {
            blackouter.notifyReinitialized(conts, Tr('history'));
        } else {
            blackouter.notifyInitialized(conts, Tr('history'));
        }

        scene.time.delayedCall(500, () => {
            listService.setBackCallback(() => {
                return new Promise<void>((resolve) => {
                    openProfile();
                    resolve();
                });
            });
        });
    }

    private initPayments(scene: Scene, playerId: number, ps: PaymentsDto, blackouter: ListBlackouter, re: boolean, listService:ListService, openProfile: () => void){
        const self = this;
        const conts: Container[] = [];

        for (const p of ps.payments) {
            const pp: PaymentAdminPanel = new PaymentAdminPanel(scene, p, playerId);
            conts.push(pp);
        }

        const pagingPanel = new PagingPanel(scene, ps.pageCurr, ps.pageMax, (page: number) => {
            PlayerStore.getPayments(playerId, page).then((ps: PaymentsDto) => {
                self.initPayments(scene, playerId, ps, blackouter, true, listService, openProfile  );
            });
        }, true);
        conts.push(pagingPanel);

        if (re) {
            blackouter.notifyReinitialized(conts, Tr('payments'));
        } else {
            blackouter.notifyInitialized(conts, Tr('payments'));
        }

        scene.time.delayedCall(500, () => {
            listService.setBackCallback(() => {
                return new Promise<void>((resolve) => {
                    openProfile();
                    resolve();
                });
            });
        });
    }

    private getProfile(playerId: number, callback: (profile: Profile) => any) {
        if (playerId == PlayerStore.getPlayer().id) {
            ProfileStore.fetchMyProfile().then((profile: Profile) => {
                callback(profile);
            });
        } else {
            ProfileStore.fetchProfileByPlayerId(playerId).then((profile: Profile) => {
                callback(profile);
            });
        }
    }

    private mutePlayer(scene: Scene, banReason: BanReason, timeInSeconds: number, playerId: number) {
        ModerStore.mutePlayer(timeInSeconds, banReason, playerId).then(() => {
            new FeedbackPanel(scene, Tr('bath.broom'), undefined, true).show();
        });
    }

    private unmutePlayer(scene: Scene, playerId: number) {
        ModerStore.unmutePlayer(playerId).then(() => {
            new FeedbackPanel(scene, Tr('player.unbanned'), undefined, true).show();
        });
    }
    
}
